import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useField, useFormikContext } from "formik";

import { getDocumentSubCategories } from "../../../actions/companyActions";
import { useCurrentCompanyValue } from "hooks/auth";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel/SelectWithLabel";

export const DocumentSubCategoryComponent = ({
	name,
	options,
	setFieldValue,
	label,
	required,
	values,
	getDocumentSubCategories,
	selectedCategory,
	disabled,
	lockedSubcategoryId,
	...props
}) => {
	const profileTypeId = useCurrentCompanyValue("profile_type")?.id;
	const [field, meta] = useField(name);
	const { setFieldTouched } = useFormikContext();
	const [optionsArray, setOptionsArray] = useState([]);

	const hasError = !!(meta.error && meta.touched);

	useEffect(() => {
		getDocumentSubCategories();
	}, [getDocumentSubCategories]);

	useEffect(() => {
		let _optionsArray = [];

		if (options.length && selectedCategory) {
			_optionsArray = options
				.filter((o) => o.parent_id === selectedCategory)
				.filter((o) =>
					selectedCategory === 3
						? o.profile_types.includes(profileTypeId)
						: true
				)
				.map((option) => ({
					value: { id: option.id, name: option.name },
					label: option.name,
				}));
		}

		setOptionsArray(_optionsArray);
	}, [selectedCategory, options, profileTypeId]);

	useEffect(() => {
		if (!optionsArray.length) {
			return;
		}

		const currentValue = values?.[name];

		if (currentValue && typeof currentValue === "object" && currentValue.id) {
			return;
		}

		if (typeof currentValue === "number") {
			const matched = optionsArray.find(
				(o) => o.value.id === currentValue
			);
			if (matched) {
				setFieldValue(name, matched.value);
			}
			return;
		}

		if (!currentValue) {
			const selectedOption = lockedSubcategoryId
				? optionsArray.find((o) => o.value.id === lockedSubcategoryId) ??
					optionsArray[0]
				: optionsArray[0];

			if (selectedOption) {
				setFieldValue(name, selectedOption.value);
			}
		}
	}, [optionsArray, lockedSubcategoryId, name, setFieldValue, values]);

	const selectedOption = useMemo(() => {
		const currentValue = field.value;
		if (
			currentValue &&
			typeof currentValue === "object" &&
			currentValue.id != null
		) {
			return (
				optionsArray.find((o) => o.value.id === currentValue.id) || null
			);
		}
		if (typeof currentValue === "number") {
			return optionsArray.find((o) => o.value.id === currentValue) || null;
		}
		return null;
	}, [field.value, optionsArray]);

	const handleChange = (option) => {
		setFieldValue(name, option ? option.value : "");
	};

	const handleBlur = () => {
		setFieldTouched(name, true);
	};

	return (
		<div
			className={props.className}
			data-testid="document-sub-category"
		>
			<SelectWithLabel
				name={name}
				label={label || "Sub Category"}
				required={!!required}
				options={optionsArray}
				value={selectedOption}
				onChange={handleChange}
				onBlur={handleBlur}
				disabled={disabled}
				hasError={hasError}
				errorText={
					hasError && typeof meta.error === "string" ? meta.error : undefined
				}
				testId={props.testId}
			/>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		options: state.companyProfile.documentSubCategories,
	};
};

export const DocumentSubCategory = connect(mapStateToProps, {
	getDocumentSubCategories,
})(DocumentSubCategoryComponent);
